import { generateHash } from '../../../utils/caching/common/key-generator';
import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';

import {
	ONE_DAY_MS_TTL,
	ONE_HOUR_MS_TTL,
	SEARCH_PERSONALIZATION_SMARTLINK_SITE_CACHE_KEY,
	SEVEN_DAY_MS_TTL,
	SMARTLINK_APP_EXPOSURE_RATE_TRAIT,
} from './constants';
import { getSmartlinkSiteTraitsUrl } from './get-smartlink-site-traits-url';
import type { Trait, Traits } from './types';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { getSmartlinkSiteTraitsUrl } from './get-smartlink-site-traits-url';

export type SmartlinkUsage = {
	[product: string]: number;
};

const {
	refreshOnAccessDecorator: refreshOnAccessDecoratorSmartlink,
	get: getSmartlinkCache,
	clear: clearSmartlinkCache,
} = promiseCache<Trait | undefined>({
	name: SEARCH_PERSONALIZATION_SMARTLINK_SITE_CACHE_KEY,
	expiration: SEVEN_DAY_MS_TTL,
});

const fetchSiteSmartlinkService = async (siteId: string): Promise<Traits> => {
	const requestUrl = getSmartlinkSiteTraitsUrl(siteId);
	const requestOptions: RequestInit = {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
		},
	};

	return fetch(requestUrl, requestOptions).then((response: Response) => {
		if (response.ok) {
			return response.json();
		}

		return Promise.reject({
			code: response.status,
			reason: response.statusText,
		});
	});
};

const fetchSmartlinkProductUsageRate = async ({
	siteId,
	analyticsCallback,
	cacheKeySuffix,
	degradationCallback = () => {},
}: {
	siteId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	cacheKeySuffix?: string;
	degradationCallback?: DegradationCallback;
}) => {
	const key = generateHash(
		`${SEARCH_PERSONALIZATION_SMARTLINK_SITE_CACHE_KEY}|${siteId}|${cacheKeySuffix}`,
	);

	const supplier = async () => {
		const fetchPromise = fetchSiteSmartlinkService(siteId)
			.then((data: Traits) => {
				const { attributes } = data;
				return attributes?.find((t) => t.name === SMARTLINK_APP_EXPOSURE_RATE_TRAIT);
			})
			.catch(() => undefined); // treat non-200 as empty and avoid caching a rejected promise

		const degrader = new DegradablePromise(fetchPromise, degradationCallback, 1000); // 1s degrade threshold

		return degrader.promise().catch(async (e) => {
			// On degrade/timeout, re-set cache entry to resolved undefined with ONE_HOUR_TTL
			// so callers won't hammer the service for the next hour, but will retry after.
			// Since we're in supplier, we can't directly set here without re-entrant complexity.
			// Instead, throw and let refreshOnAccessDecorator store this rejecting promise; after this call,
			// callers should rely on our external code path to clear or retry as needed.
			throw e;
		});
	};

	const existing = getSmartlinkCache(key.toString());
	// Choose TTL based on outcome:
	// - success (non-empty trait): default SEVEN_DAY_TTL
	// - empty (undefined): ONE_DAY_TTL
	// - degraded (timeout/reject): ONE_HOUR_TTL
	// We cannot know the outcome until supplier resolves/rejects; so we approximate:
	//   if no existing value, we assume empty path (ONE_HOUR_MS_TTL) to avoid hammering
	//   then, within supplier, we adjust by re-setting the cache on rejection with ONE_HOUR_TTL
	const effectiveExpiration = !existing?.storedValue ? ONE_HOUR_MS_TTL : undefined;
	const effectiveOnlyRefreshIfExpired = true;

	// Helper to re-write the cached value with a specific TTL without re-fetching
	const writeBackWithTTL = (value: Trait | undefined, ttl: number) => {
		refreshOnAccessDecoratorSmartlink({
			key: key.toString(),
			supplier: async () => value,
			returnExpired: false,
			onlyRefreshIfExpired: false, // force refresh to apply new TTL
			expiration: ttl,
		});
	};

	return refreshOnAccessDecoratorSmartlink({
		key: key.toString(),
		supplier: async () => {
			try {
				const result = await supplier(); // Trait | undefined (no degrade)
				if (result) {
					writeBackWithTTL(result, SEVEN_DAY_MS_TTL);
				} else {
					writeBackWithTTL(undefined, ONE_DAY_MS_TTL);
				}
				return result;
			} catch {
				// On degrade/timeout, return undefined so promiseCache stores an "empty" value
				// and then write back a short TTL to avoid hammering
				writeBackWithTTL(undefined, ONE_HOUR_MS_TTL);
				return undefined;
			}
		},
		onlyRefreshIfExpired: effectiveOnlyRefreshIfExpired,
		expiration: effectiveExpiration,
		analyticsCallback,
	});
};

// Mainly for testing, clearing cache
export const __test_clearSmartlinkCache = (key?: string): void => clearSmartlinkCache(key);

export const parseProductExposureRate = (trait?: Trait): SmartlinkUsage => {
	if (trait?.name === SMARTLINK_APP_EXPOSURE_RATE_TRAIT && typeof trait?.value === 'string') {
		const result: SmartlinkUsage = {};
		const pairs = trait.value.split(',').map((s) => s.trim());

		for (const pair of pairs) {
			const [app, rateStr] = pair.split(':').map((s) => s.trim());
			if (app && rateStr) {
				const rate = parseInt(rateStr, 10);
				if (!isNaN(rate)) {
					result[app] = rate;
				}
			}
		}

		return result;
	}
	return {};
};

/**
 * Gets the proportion of users who have smart links rendered out of all users per product in a specific tenant.
 * @param siteId - The site for which to fetch the smartlink usage.
 * @param analyticsCallback - Optional callback for cache analytics.
 * @param degradationCallback - Optional callback for handling degradation scenarios.
 * @returns A promise that resolves to an object mapping product names to their smartlink usage rates.
 */
export const getTenantSmartlinkUsage = async ({
	siteId,
	analyticsCallback,
	degradationCallback,
}: {
	siteId: string;
	analyticsCallback?: CacheAnalyticsCallback;
	degradationCallback?: DegradationCallback;
}): Promise<SmartlinkUsage> => {
	if (!siteId) {
		return {};
	}
	const trait = await fetchSmartlinkProductUsageRate({
		siteId,
		analyticsCallback,
		degradationCallback,
		cacheKeySuffix: 'rovo-search-smartlink-usage',
	});

	return parseProductExposureRate(trait);
};
